import { useEffect, useRef, useState } from "react"

import { GatsbyImage } from "gatsby-plugin-image"
import gsap from "gsap"
import styled, { css } from "styled-components"
import YTPlayer from "yt-player"

import Button from "components/buttons/Button"
import { VideoEmbeds } from "types/gatsby-alias"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"

interface Props {
  iframeData: VideoEmbeds
  className?: string
  first: boolean
  transitioning: boolean
}

export default function YouTubePlayer({
  iframeData,
  className = "",
  first,
  transitioning,
}: Props) {
  const [player, setPlayer] = useState<YTPlayer | null>(null)
  const [playerElement, setPlayerElement] = useState<HTMLDivElement | null>(
    null
  )
  const posterRef = useRef<HTMLDivElement | null>(null)

  const { youtubeVideoId, posterImage, buttonText } = iframeData ?? {}

  const handlePlay = () => {
    if (player) {
      player.play()

      gsap.to(posterRef.current, {
        opacity: 0,
        duration: 0.4,
        pointerEvents: "none",
      })
    }
  }
  useEffect(() => {
    if (player && transitioning) {
      player.pause()
    }
  }, [transitioning, player])

  useEffect(() => {
    if (playerElement && youtubeVideoId && !player) {
      const options = {
        controls: false,
        modestBranding: true,
      }
      const newPlayer = new YTPlayer(playerElement, options)
      newPlayer.load(youtubeVideoId)
      newPlayer.seek(0.5)
      setPlayer(newPlayer)
    }
  }, [playerElement, youtubeVideoId, player])

  useEffect(() => {
    if (player) {
      player.on("paused", () => {
        gsap.to(posterRef.current, {
          opacity: 1,
          duration: 0.4,
          pointerEvents: "auto",
        })
      })
    }
    return () => {
      if (player) {
        player.destroy();
      }
    };
  
  }, [player])

  return (
    <Wrapper className={className} first={first}>
      <Item ref={ref => setPlayerElement(ref)} />
      <FakePoster ref={posterRef}>
        {posterImage?.gatsbyImageData && (
          <PosterImg image={posterImage.gatsbyImageData} alt="video poster" />
        )}
        <StyledButton className="video carousel play" onClick={handlePlay} play>
          {buttonText ?? "Play Video"}
        </StyledButton>
      </FakePoster>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ first: boolean }>`
  position: absolute;
  opacity: ${({ first }) => (first ? 1 : 0)};

  ${fresponsive(css`
    width: 1144px;
    height: 740px;
    border-radius: 20px;
    margin-bottom: 25px;
  `)}

  ${ftablet(css`
    width: 834px;
    height: 600px;
  `)}

  ${fmobile(css`
    width: 335px;
    height: 260px;
    border-radius: 10px;
  `)}
`

const Item = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`

const FakePoster = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
`

const PosterImg = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
`

const StyledButton = styled(Button)`
  position: relative;
  z-index: 100;
`
